import { useMutation, useQueryClient } from "@tanstack/react-query";

import { onboardingRuns } from "../../api/endpoints/onboardingRuns";
import { qk } from "../../api/queryKeys";
import type { ExecutionKind, RunStatus } from "../../api/types/onboardingRun";
import { ErrorBanner } from "../../components/ui/ErrorBanner";

const CANCELLABLE = new Set<RunStatus>(["draft", "previewed", "approved", "queued"]);

export function RunActions({
  runId,
  status,
  executionKind,
  canWrite,
  diffReady,
}: {
  runId: string;
  status: RunStatus;
  executionKind: ExecutionKind;
  canWrite: boolean;
  diffReady: boolean;
}) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.onboardingRun(runId) });
    void qc.invalidateQueries({ queryKey: ["onboarding-runs"] });
  };
  const approve = useMutation({ mutationFn: () => onboardingRuns.approve(runId), onSuccess: invalidate });
  const queue = useMutation({ mutationFn: () => onboardingRuns.queue(runId), onSuccess: invalidate });
  const cancel = useMutation({ mutationFn: () => onboardingRuns.cancel(runId), onSuccess: invalidate });
  const reconcile = useMutation({
    mutationFn: () => onboardingRuns.reconcileRequeue(runId),
    onSuccess: invalidate,
  });
  const pending = approve.isPending || queue.isPending || cancel.isPending;
  const err = approve.error ?? queue.error ?? cancel.error;

  if (status === "needs_reconciliation") {
    // API runs are idempotent: an operator can requeue to re-run (it converges).
    // Ansible runs have no in-place requeue — create a fresh run.
    if (executionKind === "api" && canWrite) {
      return (
        <div style={{ display: "grid", gap: 8 }}>
          <p style={{ color: "var(--crit)", fontSize: "0.85rem", margin: 0 }}>
            This run needs reconciliation — state may be partially applied. Review, then requeue
            to re-run (every step is idempotent and converges).
          </p>
          <div>
            <button type="button" style={primaryBtn} disabled={reconcile.isPending}
              onClick={() => reconcile.mutate()}>Requeue (reconcile)</button>
          </div>
          {reconcile.error && <ErrorBanner error={reconcile.error} title="Reconcile failed" />}
        </div>
      );
    }
    return (
      <p style={{ color: "var(--crit)", fontSize: "0.85rem" }}>
        This run died mid-run — state may be partially applied. Investigate, then create a fresh run.
      </p>
    );
  }
  if (!canWrite) return null;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      <div style={{ display: "flex", gap: 8 }}>
        {status === "previewed" && (
          <button type="button" style={primaryBtn} disabled={pending || !diffReady}
            onClick={() => approve.mutate()}>Approve</button>
        )}
        {status === "approved" && (
          <button type="button" style={primaryBtn} disabled={pending} onClick={() => queue.mutate()}>Queue</button>
        )}
        {CANCELLABLE.has(status) && (
          <button type="button" style={dangerBtn} disabled={pending} onClick={() => cancel.mutate()}>Cancel</button>
        )}
      </div>
      {status === "previewed" && !diffReady && (
        <p style={{ color: "var(--text-dim)", fontSize: "0.8rem", margin: 0 }}>
          Approve needs loaded diff evidence — diff evidence unavailable.
        </p>
      )}
      {err && <ErrorBanner error={err} title="Action failed" />}
    </div>
  );
}

const primaryBtn: React.CSSProperties = {
  background: "var(--accent)", color: "var(--bg)", border: "none", padding: "8px 16px",
  borderRadius: 4, fontSize: "0.9rem", fontWeight: 600, cursor: "pointer",
};
const dangerBtn: React.CSSProperties = {
  background: "transparent", color: "var(--crit)", border: "1px solid var(--crit)",
  padding: "6px 12px", borderRadius: 4, fontSize: "0.85rem", cursor: "pointer",
};
